/**
 * Settings Page - General configuration for the instance
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Settings as SettingsIcon, Save, Bell, Package, DollarSign, Printer } from 'lucide-react';

import './styles.css';

export default function Settings() {
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    expiry_alert_days: 7,
    low_stock_alert_enabled: true,
    currency: 'BRL',
    timezone: 'America/Sao_Paulo',
    auto_print_receipt: false,
    require_customer_on_sale: false,
    notify_on_sale: true,
    notify_on_low_stock: true,
    notify_on_expiry: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
      }
    } catch (err) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings', settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-loading">Carregando configurações...</div>
      </div>
    );
  }

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-header">
        <div className="settings-header-title">
          <h1>
            <SettingsIcon className="h-6 w-6" />
            Configurações Gerais
          </h1>
          <p>Configure as preferências do seu restaurante</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>

      <div className="settings-grid">
        {/* Stock Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Alertas de Estoque
            </CardTitle>
            <CardDescription>
              Configure os alertas de validade e estoque mínimo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="expiry_days">Dias para alerta de validade</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="expiry_days"
                  type="number"
                  min="1"
                  max="365"
                  value={settings.expiry_alert_days}
                  onChange={(e) => handleChange('expiry_alert_days', parseInt(e.target.value) || 7)}
                  className="w-24"
                  data-testid="expiry-days-input"
                />
                <span className="text-sm text-muted-foreground">
                  dias antes do vencimento
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Produtos com validade próxima serão destacados no dashboard
              </p>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Alerta de estoque mínimo</Label>
                <p className="text-sm text-muted-foreground">
                  Notificar quando produtos atingirem estoque mínimo
                </p>
              </div>
              <Switch
                checked={settings.low_stock_alert_enabled}
                onCheckedChange={(v) => handleChange('low_stock_alert_enabled', v)}
                data-testid="low-stock-alert-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notificações
            </CardTitle>
            <CardDescription>
              Configure quais notificações você deseja receber
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notificar novas vendas</Label>
                <p className="text-sm text-muted-foreground">
                  Receber alerta quando uma venda for finalizada
                </p>
              </div>
              <Switch
                checked={settings.notify_on_sale}
                onCheckedChange={(v) => handleChange('notify_on_sale', v)}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notificar estoque baixo</Label>
                <p className="text-sm text-muted-foreground">
                  Receber alerta quando um produto estiver com estoque baixo
                </p>
              </div>
              <Switch
                checked={settings.notify_on_low_stock}
                onCheckedChange={(v) => handleChange('notify_on_low_stock', v)}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notificar produtos a vencer</Label>
                <p className="text-sm text-muted-foreground">
                  Receber alerta sobre produtos próximos da validade
                </p>
              </div>
              <Switch
                checked={settings.notify_on_expiry}
                onCheckedChange={(v) => handleChange('notify_on_expiry', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* PDV Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              PDV
            </CardTitle>
            <CardDescription>
              Configure o comportamento do ponto de venda
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Impressão automática</Label>
                <p className="text-sm text-muted-foreground">
                  Imprimir cupom automaticamente ao finalizar venda
                </p>
              </div>
              <Switch
                checked={settings.auto_print_receipt}
                onCheckedChange={(v) => handleChange('auto_print_receipt', v)}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Cliente obrigatório</Label>
                <p className="text-sm text-muted-foreground">
                  Exigir seleção de cliente para finalizar venda
                </p>
              </div>
              <Switch
                checked={settings.require_customer_on_sale}
                onCheckedChange={(v) => handleChange('require_customer_on_sale', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Regional
            </CardTitle>
            <CardDescription>
              Configure moeda e fuso horário
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Moeda</Label>
              <Select
                value={settings.currency}
                onValueChange={(v) => handleChange('currency', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRL">Real (R$)</SelectItem>
                  <SelectItem value="USD">Dólar (US$)</SelectItem>
                  <SelectItem value="EUR">Euro (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fuso Horário</Label>
              <Select
                value={settings.timezone}
                onValueChange={(v) => handleChange('timezone', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/Sao_Paulo">Brasília (GMT-3)</SelectItem>
                  <SelectItem value="America/Manaus">Manaus (GMT-4)</SelectItem>
                  <SelectItem value="America/Recife">Recife (GMT-3)</SelectItem>
                  <SelectItem value="America/Fortaleza">Fortaleza (GMT-3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
